<script>import { goto } from "$app/navigation";
import { Button } from "@infinitro/button";
import {
  favoriteGame,
  favoriteGames,
  isFavoriteGame,
  isLikedGame,
  likedGames,
  likeGame,
  refreshGamesState
} from "@infinitro/catalog-ex";
import { hasGtDemo, toGameSlug, toProviderSlug } from "@infinitro/catalog-min";
import {
  cfgCdnTiles,
  cfgToThemeContentPath,
  FeatureSet,
  gameDemoMode,
  ReleaseStage,
  stateFeatureSet,
  stateUi,
  wantAtLeastReleaseStage
} from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import {
  gameLaunchedFromPath,
  isMobile,
  locale,
  loggedIn,
  ModalDialog,
  providerBasePath,
  pushModal,
  selectedGameInfo,
  stateOpenModal,
  stateRealm,
  toSiteUrl,
  UI
} from "@infinitro/global";
import { ArrowIcon, CloseIcon } from "@infinitro/icons";
import { Text } from "@infinitro/text";
import MarkdownIt from "markdown-it";
import { onMount } from "svelte";
import { get } from "svelte/store";
import AverageRating from "./parts/review/average-rating.svelte";
import Comments from "./parts/review/comments.svelte";
import RateGame from "./parts/review/rate-game.svelte";
import TabSelector from "./parts/tab-selector.svelte";
const CDN_TILES = cfgCdnTiles();
const md = new MarkdownIt();
$: gameData = $selectedGameInfo;
$: game = gameData?.game;
$: tileSrc = gameData?.tileSrc;
let content;
let htmlContent = "";
let fetchContentError = false;
let activeTab = "description";
let hasDescription = false;
let reviewRefreshKey = 0;
let liked = false;
let favourited = false;
let realm;
stateRealm.subscribe((value) => realm = value);
$: (async () => {
  if ($favoriteGames && game) {
    favourited = await isFavoriteGame(game, { realm }, !!$loggedIn);
  } else {
    favourited = false;
  }
})();
$: (async () => {
  if ($likedGames && game) {
    liked = await isLikedGame(game, { realm }, !!$loggedIn);
  } else {
    liked = false;
  }
})();
async function handleFavouriteClick() {
  if (!$loggedIn) return;
  try {
    const provSlug = game.provider_slug || game.prov || "";
    const gSlug = game.game_slug || game.game;
    await favoriteGame(provSlug, gSlug, !!$loggedIn);
    favourited = !favourited;
    await refreshGamesState();
  } catch (error) {
    console.log("Error favoriting game:", error);
  }
}
async function handleLikeClick() {
  if (!$loggedIn) return;
  try {
    const provSlug = game.provider_slug || game.prov || "";
    const gSlug = game.game_slug || game.game;
    await likeGame(provSlug, gSlug, !!$loggedIn);
    liked = !liked;
    await refreshGamesState();
  } catch (error) {
    console.log("Error liking game:", error);
  }
}
$: isPreviewMode = wantAtLeastReleaseStage(ReleaseStage.Alpha) && $stateFeatureSet === FeatureSet.Preview;
$: useGamePathInfoModalFlow = $isMobile && $stateFeatureSet === FeatureSet.Preview && $stateUi.games.showGameInfoModalOnGamePathMobile;
function handleReviewUpdate(event) {
  reviewRefreshKey += 1;
}
function standardizeGameName(name) {
  return name.toLowerCase().replace(/\s+/g, "-");
}
async function fetchContent() {
  try {
    const response = await fetch(content);
    if (!response.ok) {
      fetchContentError = true;
      hasDescription = false;
      if (isPreviewMode) {
        activeTab = "review";
      }
      throw new Error(`Error fetching content: ${response.statusText}`);
    }
    let text = await response.text();
    text = text.replace(/%(domain|brand)%/g, "Flush");
    htmlContent = md.render(text).replace(/<\/?h1(\s|>)/g, (m) => m.replace("h1", "h2"));
    htmlContent = htmlContent.replace(new RegExp("<p>", "g"), '<p style="font-weight: 400">');
    htmlContent = htmlContent.replace(new RegExp("<li>", "g"), '<li style="font-weight: 400">');
    htmlContent = htmlContent.replace(
      new RegExp("<table>", "g"),
      '<div style="overflow-x: auto; margin-bottom: 5px"><table>'
    );
    htmlContent = htmlContent.replace(new RegExp("</table>", "g"), "</table></div>");
    hasDescription = true;
  } catch (error) {
    fetchContentError = true;
    hasDescription = false;
    if (isPreviewMode) {
      activeTab = "review";
    }
    htmlContent = "";
  }
}
$: {
  if (game && game.game) {
    let standardizedGame = standardizeGameName(game.game);
    let contentBase = cfgToThemeContentPath(
      `/${game.provider_slug}/${standardizedGame}/${standardizedGame}.${$locale}.md`
    );
    content = contentBase;
    fetchContent();
  }
}
function closeModal(clearGameInfo = true) {
  stateOpenModal.set(void 0);
  if (clearGameInfo) {
    selectedGameInfo.set(void 0);
  }
}
function handleSignIn() {
  closeModal();
  pushModal(ModalDialog.Auth, { tab: "signup" });
}
function getGameUrl() {
  if (!game) return "#";
  const provider = game.prov ?? "";
  const gameSlug = provider.toLowerCase() === "sports" ? game.game : toGameSlug(game.game);
  return toSiteUrl(
    `${providerBasePath(provider)}${toProviderSlug(provider)}/${gameSlug}`,
    $loggedIn
  ) || "#";
}
function handleModalClose() {
  if (useGamePathInfoModalFlow) {
    const previousPath = get(gameLaunchedFromPath);
    closeModal();
    if (previousPath && previousPath !== window.location.pathname) {
      goto(previousPath);
    } else {
      goto(toSiteUrl("/casino", $loggedIn));
    }
    return;
  }
  closeModal();
}
function shouldLaunchInCurrentPath(url) {
  if (!url || url === "#") return false;
  const launchPath = new URL(url, window.location.origin).pathname;
  return launchPath === window.location.pathname;
}
function handleDemoPlay() {
  gameDemoMode.set(true);
  gameLaunchedFromPath.set(window.location.pathname);
  const url = getGameUrl();
  if (useGamePathInfoModalFlow && shouldLaunchInCurrentPath(url)) {
    selectedGameInfo.update((value) => ({ ...value ?? {}, launchIntent: "demo" }));
    closeModal(false);
    return;
  }
  closeModal();
  if (url !== "#") {
    goto(url);
  }
}
function handleRealPlay() {
  gameDemoMode.set(false);
  gameLaunchedFromPath.set(window.location.pathname);
  const url = getGameUrl();
  if (useGamePathInfoModalFlow && shouldLaunchInCurrentPath(url)) {
    selectedGameInfo.update((value) => ({ ...value ?? {}, launchIntent: "real" }));
    closeModal(false);
    return;
  }
  closeModal();
  if (url !== "#") {
    goto(url);
  }
}
onMount(async () => {
  if (game && game.game) {
    fetchContent();
  }
  if (game && $loggedIn) {
    try {
      favourited = await isFavoriteGame(game, { realm }, !!$loggedIn);
      liked = await isLikedGame(game, { realm }, !!$loggedIn);
    } catch (error) {
      console.log("Error fetching game fav/like state:", error);
    }
  }
});
</script>

{#if game}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<div class="modal-backdrop game-info-modal" on:click={handleModalClose}>
		<div class="modal-container" on:click|stopPropagation>
			<!-- Close Button -->
			<button class="close-button" on:click={handleModalClose} aria-label="Close">
				<CloseIcon />
			</button>

			<!-- Top Section: Game Tile + Info + Buttons -->
			<div class="game-info-section">
				<div class="game-tile-container">
					<img src={tileSrc} alt={game.game} class="game-tile-image" />
				</div>
				<div class="game-details-container">
					<div class="game-text-info">
						<Text type="subtitle" subtitleSize="medium" subtitleWeight="bold"
							>{$locale === 'ja'
								? (game.ja ?? game.game)
								: $locale === 'ko'
									? (game.ko ?? game.game)
									: game.game}</Text
						>
						<Text type="body" bodySize="small"
							>{$t(Verbiage.by)} <span class="bold-text">{game.prov ?? ''}</span></Text
						>
					</div>
					<div class="game-action-buttons">
						{#if !$loggedIn}
							<Button
								width="100%"
								variant="primary"
								text={$t(Verbiage.login)}
								size="small"
								onClick={handleSignIn}
							/>
							<Button
								disabled={!game || !game.f || !hasGtDemo(game.f)}
								width="100%"
								variant="secondary"
								text={$t(Verbiage.demo_play)}
								size="small"
								onClick={handleDemoPlay}
							/>
						{:else}
							<Button
								width="100%"
								variant="primary"
								text={$t(Verbiage.play)}
								size="small"
								onClick={handleRealPlay}
							/>
							{#if game && game.f && hasGtDemo(game.f)}
								<Button
									width="100%"
									variant="secondary"
									text={$t(Verbiage.demo_play)}
									size="small"
									onClick={handleDemoPlay}
								/>
							{/if}
						{/if}
					</div>
				</div>
			</div>

			<!-- Bottom Section: Content -->
			<div class="accordion-section">
				<div class="custom-dropdown custom-dropdown-open">
					<div class="dropdown-toggle">
						<div class="d-flex-col title-name">
							<Text className="info-accordion-game-name-text" type="title" titleSize="h2"
								>{$locale === 'ja'
									? (game.ja ?? game.game)
									: $locale === 'ko'
										? (game.ko ?? game.game)
										: game.game}</Text
							>
							<Text type="body" bodySize="medium"
								>{$t(Verbiage.by)} <span class="bold-text">{game.prov ?? ''}</span></Text
							>
						</div>
					</div>

					{#if $loggedIn}
						<div class="fav-like-row">
							<button
								type="button"
								class="fav-like-button {favourited ? 'active' : ''}"
								on:click={handleFavouriteClick}
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M7.49009 0.332066C7.69867 -0.110689 8.30133 -0.110688 8.50991 0.332067L10.5415 4.64464C10.6244 4.82046 10.7845 4.94232 10.9697 4.97051L15.5125 5.66206C15.9789 5.73306 16.1651 6.33352 15.8277 6.67815L12.5404 10.035C12.4064 10.1719 12.3453 10.3691 12.3769 10.5623L13.1529 15.3023C13.2326 15.7889 12.745 16.16 12.3279 15.9302L8.2646 13.6923C8.09895 13.6011 7.90105 13.6011 7.7354 13.6923L3.67215 15.9302C3.25499 16.16 2.76743 15.7889 2.8471 15.3023L3.62311 10.5623C3.65475 10.3691 3.5936 10.1719 3.45958 10.035L0.172339 6.67815C-0.165149 6.33352 0.0210819 5.73306 0.487479 5.66206L5.03033 4.97051C5.21554 4.94232 5.37564 4.82046 5.45847 4.64464L7.49009 0.332066Z"
										fill={favourited ? '#FF9E14' : 'white'}
									/>
								</svg>
								<span>{$t(Verbiage.favourite)}</span>
							</button>
							{#if $stateUi.games?.showLikeButton}
								<button
									type="button"
									class="fav-like-button {liked ? 'active' : ''}"
									on:click={handleLikeClick}
								>
									<svg
										width="18"
										height="16"
										viewBox="0 0 18 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M12.996 0C11.367 0 9.909 0.791011 9 2.00449C8.091 0.791011 6.633 0 5.004 0C2.241 0 0 2.24719 0 5.02472C0 6.09438 0.171 7.08315 0.468 8C1.89 12.4944 6.273 15.182 8.442 15.9191C8.748 16.027 9.252 16.027 9.558 15.9191C11.727 15.182 16.11 12.4944 17.532 8C17.829 7.08315 18 6.09438 18 5.02472C18 2.24719 15.759 0 12.996 0Z"
											fill={liked ? '#FF375F' : 'white'}
										/>
									</svg>
									<span>{$t(Verbiage.like)}</span>
								</button>
							{/if}
						</div>
					{/if}

					<div class="dropdown-menu expanded">
						<div class="game-content">
							<div class="game-description">
								{#if hasDescription && isPreviewMode}
									<TabSelector
										tabs={[
											{ id: 'description', label: $t(Verbiage.description) || 'Description' },
											{ id: 'review', label: 'Review' }
										]}
										bind:activeTab
									/>
								{/if}

								{#if isPreviewMode}
									{#if !hasDescription}
										<div class="review-content">
											<div class="review-cards-row">
												<RateGame {game} on:reviewUpdated={handleReviewUpdate} />
												{#key reviewRefreshKey}
													<AverageRating {game} />
												{/key}
											</div>
											<Comments
												gameSlug={toGameSlug(game.game)}
												providerSlug={game.provider_slug}
											/>
										</div>
									{:else if activeTab === 'description'}
										{#if htmlContent}
											<div class="game-info-markdown">{@html htmlContent}</div>
										{/if}
									{:else if activeTab === 'review'}
										<div class="review-content">
											<div class="review-cards-row">
												<RateGame {game} on:reviewUpdated={handleReviewUpdate} />
												{#key reviewRefreshKey}
													<AverageRating {game} />
												{/key}
											</div>
											<Comments
												gameSlug={toGameSlug(game.game)}
												providerSlug={game.provider_slug}
											/>
										</div>
									{/if}
								{:else if hasDescription && htmlContent}
									<div class="game-info-markdown">{@html htmlContent}</div>
								{/if}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	@keyframes slideUp {
		from {
			transform: translateY(100%);
			opacity: 0;
		}
		to {
			transform: translateY(0);
			opacity: 1;
		}
	}

	.game-info-modal {
		display: block;
}

	@media (min-width: 1024px) {
		.game-info-modal {
				display: none;
		}
}

	.modal-backdrop {
		position: fixed;
		inset: 0px;
		z-index: 9999;
		display: flex;
		align-items: flex-end;
		justify-content: center;
		background-color: rgb(0 0 0 / var(--tw-bg-opacity));
		--tw-bg-opacity: 0.5;
}

	@media (min-width: 1024px) {
		.modal-backdrop {
				align-items: center;
		}
	}

	.modal-container {
		position: relative;
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		overflow-y: auto;
		border-width: 1.5px;
		border-color: var(--color-background-300);
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-100);
		--tw-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
		--tw-shadow-colored: 0 1px 3px 0 var(--tw-shadow-color), 0 1px 2px -1px var(--tw-shadow-color);
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		font-family: var(--font-main);
		background-image:
			radial-gradient(circle at 50% 0%, var(--light-color), transparent 50%),
			radial-gradient(circle at 50% 100%, var(--light-color), transparent 50%),
			linear-gradient(to bottom, var(--color-background-700), var(--color-background-700)),
			linear-gradient(to bottom, rgba(255, 255, 255, 0.22), var(--color-neutral-900));
		background-origin: border-box;
		background-clip: content-box, border-box;
		border: double 1px transparent;
		box-shadow: 0 0 0 2px var(--color-neutral-900) !important;
		--light-color: color-mix(in srgb, var(--color-secondary-700) 50%, transparent);
		--light-color: rgb(from var(--color-secondary-700) r g b / 0.5);
		animation: slideUp 0.5s ease-out;
		min-height: 100vh;
		min-height: 100dvh;
}

	@media (min-width: 1024px) {
		.modal-container {
				border-radius: 24px;
			max-width: 90vw;
			max-height: 95vh;
			min-height: 0;
			width: 800px;
		}
	}

	.close-button {
		position: absolute;
		top: 0px;
		right: 0px;
		z-index: 150;
		margin: 1rem;
		display: flex;
		height: 24px;
		width: 24px;
		align-items: center;
		justify-content: center;
		border-radius: 8px;
		border-top-width: 1px;
		border-color: #3F4B7980;
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 500;
		--tw-text-opacity: 1;
		color: rgb(209 213 219 / var(--tw-text-opacity));
		background-image: linear-gradient(
			180deg,
			var(--color-background-300) 0%,
			var(--color-background-700) 100%
		);
}

	.close-button:hover {
		--tw-border-opacity: 1;
		border-color: rgb(156 163 175 / var(--tw-border-opacity));
		--tw-text-opacity: 1;
		color: rgb(229 231 235 / var(--tw-text-opacity));
}

	.game-info-section {
		display: flex;
		gap: 1rem;
		border-bottom-width: 1px;
		border-color: var(--color-neutral-900);
		padding: 1rem;
}

	.game-tile-container {
		height: 8rem;
		width: 6rem;
		flex-shrink: 0;
		overflow: hidden;
		border-radius: 0.5rem;
}

	.game-tile-image {
		height: 100%;
		width: 100%;
		-o-object-fit: cover;
		   object-fit: cover;
}

	.game-details-container {
		display: flex;
		flex: 1 1 0%;
		flex-direction: column;
		justify-content: space-between;
}

	.game-text-info {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
}

	.game-action-buttons {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
}

	.accordion-section {
		flex: 1 1 0%;
		overflow-y: auto;
}

	.custom-dropdown {
		position: relative;
		height: -moz-fit-content;
		height: fit-content;
		width: 100%;
		border-width: 0px;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 600;
		background: var(--gradient-main);
}

	.custom-dropdown-open {
		background: var(--gradient-alternative);
}

	.dropdown-toggle {
		position: relative;
		z-index: 10;
		display: block;
		min-height: 88px;
		width: 100%;
		padding: 1rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		letter-spacing: 1.5px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity));
		font-family: var(--font-main);
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	.dropdown-menu {
		position: relative;
		margin-top: -1.5rem;
		width: 100%;
		overflow: hidden;
		border-radius: 0.5rem;
		background-color: transparent;
		padding-left: 0px;
		padding-right: 0px;
		padding-top: 1.25rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity));
		transition-property: all;
		transition-duration: 150ms;
		transition-duration: 500;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		max-height: 0;
}

	.dropdown-menu.expanded {
		max-height: 10000px;
	}

	.d-flex-col {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		text-align: start;
}

	.title-name {
		display: flex;
		white-space: nowrap;
}

	.bold-text {
		font-weight: 700;
}

	.fav-like-row {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		padding-left: 1rem;
		padding-right: 1rem;
		padding-bottom: 1rem;
}

	.fav-like-button {
		display: flex;
		cursor: pointer;
		align-items: center;
		gap: 0.5rem;
		border-radius: 9999px;
		border-width: 1px;
		border-color: var(--color-background-300);
		background-color: var(--color-background-500);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 500;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity));
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
}

	.fav-like-button:hover {
		border-color: var(--color-text-100);
		background-color: var(--color-background-300);
}

	.fav-like-button.active {
		border-color: var(--color-primary-500);
}

	.game-content {
		position: relative;
		margin-left: auto;
		margin-right: auto;
		width: 100%;
		padding: 1rem;
}

	.game-description {
		position: relative;
		z-index: 30;
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 1rem;
}

	.game-info-markdown {
		margin-bottom: 1rem;
		border-radius: 0.5rem;
		padding: 1rem;
}

	.no-description {
		padding-top: 2rem;
		padding-bottom: 2rem;
		text-align: center;
}

	.review-content {
		display: flex;
		flex-direction: column;
		gap: 1rem;
}

	.review-cards-row {
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 1rem;
}

	@media (min-width: 1024px) {
		.review-cards-row {
				flex-direction: row;
		}
		.review-cards-row > :global(*:first-child) {
				width: 30%;
		}
		.review-cards-row > :global(*:last-child) {
				flex: 1 1 0%;
		}
}

	:global(.info-accordion-game-name-text) {
		font-size: 1.125rem !important;
		font-weight: 600 !important;
		line-height: normal !important;
	}

	:global(.game-info-markdown h1),
	:global(.game-info-markdown h2),
	:global(.game-info-markdown h3),
	:global(.game-info-markdown h4),
	:global(.game-info-markdown h5),
	:global(.game-info-markdown h6) {
		margin-bottom: 2rem;
		font-weight: 700;
}

	:global(.game-info-markdown h3) {
		font-size: 20px;
}

	:global(.game-info-markdown h1) {
		line-height: 28px;
}

	:global(.game-info-markdown h2) {
		line-height: 24px;
}

	:global(.game-info-markdown h3) {
		line-height: 22px;
}

	:global(.game-info-markdown h4) {
		line-height: 20px;
}

	:global(.game-info-markdown h5) {
		line-height: 18px;
}

	:global(.game-info-markdown h6) {
		line-height: 16px;
}

	:global(.game-info-markdown p),
	:global(.game-info-markdown ul),
	:global(.game-info-markdown ol) {
		margin-bottom: 2rem;
		font-size: 16px;
		font-weight: 100;
}

	:global(.game-info-markdown li) {
		margin-bottom: 1rem;
}

	:global(.game-info-markdown hr) {
		margin-bottom: 2rem;
		height: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-neutral-900);
}

	:global(.game-info-markdown table) {
		margin-bottom: 1rem;
		width: 100%;
		border-collapse: collapse;
		border-radius: 0.5rem;
		border-width: 1px;
		border-color: var(--color-neutral-900);
}

	:global(.game-info-markdown th),
	:global(.game-info-markdown td) {
		border-width: 1px;
		border-color: var(--color-neutral-900);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		text-align: left;
		background: var(--color-background-500);
}

	:global(.game-info-markdown th) {
		background: var(--color-background-500);
		font-size: 16px;
		font-weight: 700;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity));
	}

	:global(.game-info-markdown td) {
		font-size: 12px;
		font-weight: 100;
}
</style>
